import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Plus, User } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const STATUS_COLORS = {
  submitted: "bg-scu-purple/10 text-scu-purple border-scu-purple/30",
  under_review: "bg-amber-100 text-amber-800 border-amber-300",
  approved: "bg-emerald-100 text-emerald-800 border-emerald-300",
  waitlisted: "bg-blue-100 text-blue-800 border-blue-300",
  paid: "bg-emerald-600/10 text-emerald-700 border-emerald-600/30",
};

export default function ParentDashboard() {
  const { user } = useAuth();
  const [athletes, setAthletes] = useState([]);
  const [events, setEvents] = useState([]);
  const [ann, setAnn] = useState([]);

  useEffect(() => {
    if (user?.role === "admin") return;
    api.get("/athletes/mine").then(r => setAthletes(r.data)).catch(() => {});
    api.get("/events").then(r => setEvents(r.data.slice(0, 5))).catch(() => {});
    api.get("/announcements").then(r => setAnn(r.data.slice(0, 3))).catch(() => {});
  }, [user]);

  if (user?.role === "admin") {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-4">
        <div className="text-center">
          <h2 className="font-bebas text-4xl tracking-wide text-scu-obsidian">You're signed in as Admin</h2>
          <Button asChild className="mt-6 bg-scu-purple hover:bg-scu-electric text-white font-bold uppercase tracking-widest">
            <Link to="/admin">Go to Admin Dashboard</Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="parent-dashboard" className="bg-scu-off min-h-[80vh]">
      <div className="border-b border-scu-smoke/15 bg-white">
        <div className="mx-auto max-w-[1400px] px-4 lg:px-8 py-8 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-scu-purple font-bold">Parent Portal</div>
            <h1 className="mt-1 font-bebas text-4xl md:text-5xl tracking-wide text-scu-obsidian">Welcome, {user?.name}</h1>
          </div>
          <Button data-testid="parent-add-athlete" asChild className="bg-scu-purple hover:bg-scu-electric text-white font-bold uppercase tracking-widest">
            <Link to="/registration"><Plus className="mr-2 h-4 w-4" /> Register Athlete</Link>
          </Button>
        </div>
      </div>

      <div className="mx-auto max-w-[1400px] px-4 lg:px-8 py-10 grid lg:grid-cols-3 gap-6">
        <section className="lg:col-span-2 space-y-4">
          <div className="text-xs uppercase tracking-[0.3em] text-scu-smoke font-bold">Your Athletes</div>
          {athletes.length === 0 && (
            <Card className="border-dashed border-scu-smoke/30 p-10 text-center">
              <User className="h-8 w-8 text-scu-smoke/60 mx-auto" />
              <div className="mt-3 font-bebas text-2xl tracking-wide text-scu-obsidian">No athletes yet</div>
              <div className="text-sm text-scu-smoke">Start a registration to add your child.</div>
              <Button asChild className="mt-4 bg-scu-purple hover:bg-scu-electric text-white font-bold uppercase tracking-widest">
                <Link to="/registration">Start Registration</Link>
              </Button>
            </Card>
          )}
          {athletes.map((a) => (
            <Card key={a.id} data-testid={`parent-athlete-${a.id}`} className="border-scu-smoke/15 p-6">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                <div>
                  <div className="font-bebas text-3xl tracking-wide text-scu-obsidian">{a.participant_full_name || `${a.first_name} ${a.last_name}`}</div>
                  <div className="text-sm text-scu-smoke">
                    {(a.program || a.sport) === "football" ? "Football" : "Cheer"} · {a.division || a.requested_division} · Grade {a.grade_2027 || a.grade}
                  </div>
                  {a.team_name ? (
                    <div className="mt-2 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded border bg-scu-purple/10 text-scu-purple border-scu-purple/30" data-testid={`parent-team-${a.id}`}>
                      Team · {a.team_name}
                    </div>
                  ) : (
                    <div className="mt-2 text-[10px] uppercase tracking-widest text-scu-smoke/70 font-bold">Team assignment pending</div>
                  )}
                  {a.media_consent && (
                    <div className="text-xs text-scu-smoke/80 mt-1">Media: {a.media_consent === "consent" ? "Consented" : "Do Not Consent"}</div>
                  )}
                </div>
                <div className="flex flex-col md:items-end gap-1">
                  <div className={`inline-block text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded border ${STATUS_COLORS[a.status] || 'bg-scu-smoke/10 text-scu-smoke border-scu-smoke/20'}`}>
                    {(a.status || "submitted").replace('_', ' ')}
                  </div>
                  <div className="text-xs text-scu-smoke">Payment: {a.payment_status || "pending"}</div>
                </div>
              </div>
            </Card>
          ))}
        </section>

        <aside className="space-y-6">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-scu-smoke font-bold">Upcoming Events</div>
            <div className="mt-3 divide-y divide-scu-smoke/15 border border-scu-smoke/15 rounded-md bg-white">
              {events.length === 0 && <div className="p-4 text-sm text-scu-smoke">No upcoming events yet.</div>}
              {events.map((e) => (
                <div key={e.id} className="p-4">
                  <div className="text-[10px] uppercase tracking-widest font-bold text-scu-purple">{e.event_type} · {e.sport}</div>
                  <div className="text-sm font-bold text-scu-obsidian">{e.title}</div>
                  <div className="text-xs text-scu-smoke">{new Date(e.date).toLocaleDateString()} · {e.time}</div>
                </div>
              ))}
            </div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-scu-smoke font-bold">Announcements</div>
            <div className="mt-3 space-y-3">
              {ann.length === 0 && <div className="p-4 border border-scu-smoke/15 rounded-md bg-white text-sm text-scu-smoke">No announcements yet.</div>}
              {ann.map((a) => (
                <Card key={a.id} className="border-scu-smoke/15 p-4">
                  <div className="text-[10px] uppercase tracking-widest font-bold text-scu-purple">{a.category}</div>
                  <div className="text-sm font-bold text-scu-obsidian">{a.title}</div>
                  <div className="text-xs text-scu-smoke mt-1">{a.body.slice(0, 100)}{a.body.length > 100 ? '…' : ''}</div>
                </Card>
              ))}
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
